import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import {
  Send, ListChecks, Users, Eye, MousePointer, Reply,
  TrendingUp, TrendingDown, Minus, ShieldCheck, AlertTriangle, Trophy,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";

const fmt = (n) => {
  if (n == null) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1) + "k";
  return String(n);
};

const TrendChip = ({ value }) => {
  if (value == null) return null;
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  const tone = value >= 30 ? "text-emerald-400" : value >= 10 ? "text-amber-300" : "text-zinc-500";
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-[10px] ${tone}`}>
      <Icon size={10} /> {value}%
    </span>
  );
};

const Kpi = ({ label, value, sub, icon: Icon, testid, accent = false }) => (
  <div
    className={`p-5 bg-[#0A0A0A] border-l-2 ${accent ? "border-[#E23A28]" : "border-transparent"} hover:border-zinc-700 transition-colors`}
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// {label}</div>
      {Icon && <Icon size={13} className="text-zinc-600" />}
    </div>
    <div className="flex items-baseline gap-2 mt-3">
      <div className="font-display text-[28px] leading-none font-semibold tracking-tight text-white tabular-nums">{value}</div>
      {sub && <div className="font-mono text-[11px] text-zinc-500">{sub}</div>}
    </div>
  </div>
);

const RateCard = ({ label, rate, count, color = "#E23A28", testid }) => {
  const data = [{ name: label, value: Math.min(100, rate || 0), fill: color }];
  return (
    <div className="p-5 bg-[#0A0A0A] flex items-center gap-4" data-testid={testid}>
      <div style={{ width: 78, height: 78 }} className="flex-shrink-0">
        <ResponsiveContainer>
          <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar background={{ fill: "#1a1a1a" }} dataKey="value" cornerRadius={2} />
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// {label}</div>
        <div className="flex items-baseline gap-2 mt-1">
          <div className="font-display text-2xl font-semibold text-white tabular-nums">{rate != null ? rate.toFixed(1) : "0"}<span className="text-zinc-500 text-base">%</span></div>
        </div>
        <div className="font-mono text-[10px] text-zinc-500 mt-1">{fmt(count)} events</div>
      </div>
    </div>
  );
};

const tooltipStyle = {
  background: "#0A0A0A",
  border: "1px solid #27272a",
  borderRadius: 0,
  color: "#fff",
  fontFamily: "JetBrains Mono, monospace",
  fontSize: 11,
};

const dayLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit" });
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="p-10 text-zinc-500 font-mono text-sm">// loading dashboard...</div>;
  }

  const a = data.aggregate || {};
  const rates = data.rates || {};
  const delivery = data.delivery || {};
  const series = (data.timeseries || []).map((d) => ({ ...d, label: dayLabel(d.date) }));
  const last14 = series.reduce(
    (acc, d) => ({
      sent: acc.sent + (d.sent || 0),
      opened: acc.opened + (d.opened || 0),
      clicked: acc.clicked + (d.clicked || 0),
      replied: acc.replied + (d.replied || 0),
    }),
    { sent: 0, opened: 0, clicked: 0, replied: 0 },
  );
  const recent = data.recent_campaigns || [];
  const top = data.top_campaigns || [];

  return (
    <div className="p-8 lg:p-10 max-w-[1400px] cf-fade">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8 pb-5 border-b border-zinc-800">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// overview / last 14 days</div>
          <h1 className="font-display text-3xl font-semibold mt-1 tracking-tight text-white">Dashboard</h1>
          <div className="text-zinc-500 text-xs mt-1 font-mono">
            {last14.sent.toLocaleString()} sends &middot; {fmt(last14.opened)} opens &middot; {fmt(last14.clicked)} clicks &middot; {fmt(last14.replied)} replies
          </div>
        </div>
        <button data-testid="quick-create-campaign" onClick={() => nav("/campaigns/new")} className="cf-btn-primary">
          + New Campaign
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900 mb-6">
        <Kpi label="Campaigns" value={fmt(data.campaigns_count)} sub={data.campaigns_count === 1 ? "campaign" : "campaigns"} icon={Send} testid="metric-campaigns" />
        <Kpi label="Lists" value={fmt(data.lists_count)} sub={`${fmt(data.contacts_total)} contacts`} icon={ListChecks} testid="metric-lists" />
        <Kpi label="Contacts" value={fmt(data.contacts_total)} sub="across all lists" icon={Users} testid="metric-contacts" />
        <Kpi label="Total Sent" value={fmt(a.sent)} sub="all-time" icon={Send} testid="metric-sent" accent />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900 mb-8">
        <RateCard label="Open rate" rate={rates.open_rate} count={a.opened} color="#3b82f6" testid="rate-open" />
        <RateCard label="Click rate" rate={rates.click_rate} count={a.clicked} color="#a855f7" testid="rate-click" />
        <RateCard label="Reply rate" rate={rates.reply_rate} count={a.replied} color="#22c55e" testid="rate-reply" />
        <RateCard label="CTR (of opens)" rate={rates.click_through_rate} count={a.clicked} color="#E23A28" testid="rate-ctr" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900 mb-8">
        <div className="bg-[#0A0A0A] p-5 lg:col-span-2" data-testid="chart-engagement">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// engagement / 14d</div>
              <h2 className="font-display text-base font-semibold text-white mt-0.5">Send & engagement trend</h2>
            </div>
            <div className="flex items-center gap-3 text-[10px] font-mono">
              <span className="flex items-center gap-1.5 text-zinc-400"><span className="w-2 h-2 bg-[#E23A28] inline-block"></span>SENT</span>
              <span className="flex items-center gap-1.5 text-zinc-400"><span className="w-2 h-2 bg-[#3b82f6] inline-block"></span>OPEN</span>
              <span className="flex items-center gap-1.5 text-zinc-400"><span className="w-2 h-2 bg-[#a855f7] inline-block"></span>CLICK</span>
              <span className="flex items-center gap-1.5 text-zinc-400"><span className="w-2 h-2 bg-[#22c55e] inline-block"></span>REPLY</span>
            </div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={series} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradSent" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E23A28" stopOpacity={0.45} /><stop offset="100%" stopColor="#E23A28" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gradOpen" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} /><stop offset="100%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gradClick" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#a855f7" stopOpacity={0.3} /><stop offset="100%" stopColor="#a855f7" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gradReply" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} /><stop offset="100%" stopColor="#22c55e" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#52525b", fontSize: 10, fontFamily: "JetBrains Mono, monospace" }} axisLine={{ stroke: "#1a1a1a" }} tickLine={false} />
                <YAxis tick={{ fill: "#52525b", fontSize: 10, fontFamily: "JetBrains Mono, monospace" }} axisLine={{ stroke: "#1a1a1a" }} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: "#27272a" }} />
                <Area type="monotone" dataKey="sent" stroke="#E23A28" strokeWidth={2} fill="url(#gradSent)" />
                <Area type="monotone" dataKey="opened" stroke="#3b82f6" strokeWidth={1.5} fill="url(#gradOpen)" />
                <Area type="monotone" dataKey="clicked" stroke="#a855f7" strokeWidth={1.5} fill="url(#gradClick)" />
                <Area type="monotone" dataKey="replied" stroke="#22c55e" strokeWidth={1.5} fill="url(#gradReply)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#0A0A0A] p-5" data-testid="chart-delivery">
          <div className="mb-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// delivery / health</div>
            <h2 className="font-display text-base font-semibold text-white mt-0.5">Deliverability</h2>
          </div>
          <div className="space-y-4">
            <DeliveryBar label="Delivered" icon={ShieldCheck} value={delivery.delivered} total={a.sent} color="#22c55e" testid="delivery-delivered" />
            <DeliveryBar label="Bounced" icon={AlertTriangle} value={delivery.bounced} total={a.sent} color="#f59e0b" testid="delivery-bounced" />
            <DeliveryBar label="Marked spam" icon={AlertTriangle} value={delivery.spam} total={a.sent} color="#E23A28" testid="delivery-spam" />
          </div>
          <div className="mt-5 pt-4 border-t border-zinc-900 grid grid-cols-2 gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Delivery rate</div>
              <div className="font-display text-xl font-semibold text-emerald-400 mt-1 tabular-nums">{(delivery.delivery_rate ?? 0).toFixed(1)}%</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Bounce rate</div>
              <div className="font-display text-xl font-semibold text-amber-400 mt-1 tabular-nums">{(delivery.bounce_rate ?? 0).toFixed(1)}%</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
        <div className="bg-[#0A0A0A] p-5" data-testid="top-campaigns">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// top / open_rate</div>
              <h2 className="font-display text-base font-semibold text-white mt-0.5 flex items-center gap-2">
                <Trophy size={14} className="text-amber-300" /> Top performers
              </h2>
            </div>
          </div>
          {top.length === 0 ? (
            <div className="text-zinc-600 text-sm font-mono py-6 text-center">// no sends yet</div>
          ) : (
            <div className="space-y-3">
              {top.map((c, i) => (
                <button key={c.id} type="button" onClick={() => nav(`/campaigns/${c.id}`)} className="w-full text-left group" data-testid={`top-campaign-${c.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="font-mono text-[10px] text-zinc-600 w-4 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-white text-sm truncate group-hover:text-[#E23A28] transition-colors">{c.name}</span>
                    </div>
                    <TrendChip value={c.open_rate} />
                  </div>
                  <div className="ml-6 mt-1.5 h-1 bg-zinc-900 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300" style={{ width: `${Math.min(100, c.open_rate)}%` }} />
                  </div>
                  <div className="ml-6 mt-1 flex items-center gap-3 font-mono text-[10px] text-zinc-500">
                    <span>{fmt(c.sent)} sent</span><span>&middot;</span><span>{c.click_rate}% click</span><span>&middot;</span><span>{c.reply_rate}% reply</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-[#0A0A0A] p-5 lg:col-span-2" data-testid="recent-campaigns">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// recent / activity</div>
              <h2 className="font-display text-base font-semibold text-white mt-0.5">Recent campaigns</h2>
            </div>
            <button type="button" onClick={() => nav("/campaigns")} className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white" data-testid="view-all-campaigns">View all &rarr;</button>
          </div>
          {recent.length === 0 ? (
            <div className="text-zinc-600 text-sm font-mono py-12 text-center" data-testid="no-campaigns-yet">// no campaigns yet — create your first one to see stats here</div>
          ) : (
            <div className="divide-y divide-zinc-900">
              {recent.map((c) => {
                const st = c.stats || {};
                const openRate = st.sent ? Math.round((st.opened / st.sent) * 100) : 0;
                return (
                  <button key={c.id} type="button" onClick={() => nav(`/campaigns/${c.id}`)} className="w-full text-left py-3 group flex items-center justify-between gap-4" data-testid={`recent-campaign-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white text-sm truncate group-hover:text-[#E23A28] transition-colors">{c.name}</span>
                        <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border ${
                          c.status === "sent" ? "border-emerald-500/40 text-emerald-400"
                            : c.status === "sending" ? "border-amber-500/40 text-amber-400"
                              : c.status === "scheduled" ? "border-blue-500/40 text-blue-400"
                                : "border-zinc-700 text-zinc-500"
                        }`}>{c.status}</span>
                      </div>
                      <div className="font-mono text-[10px] text-zinc-500 mt-1 truncate">{c.list_name || "—"}</div>
                    </div>
                    <div className="hidden md:flex items-center gap-5 text-right font-mono text-xs tabular-nums">
                      <div><div className="text-[9px] text-zinc-600 uppercase">Sent</div><div className="text-white">{fmt(st.sent || 0)}</div></div>
                      <div><div className="text-[9px] text-zinc-600 uppercase">Open</div><div className="text-white">{fmt(st.opened || 0)}</div></div>
                      <div className="w-24">
                        <div className="text-[9px] text-zinc-600 uppercase">Open %</div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1 bg-zinc-900 overflow-hidden"><div className="h-full bg-[#E23A28]" style={{ width: `${Math.min(100, openRate)}%` }} /></div>
                          <div className="text-white text-[11px] w-8">{openRate}%</div>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DeliveryBar({ label, value = 0, total = 0, color, icon: Icon, testid }) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
          {Icon && <Icon size={11} />} {label}
        </span>
        <span className="font-mono text-[11px] text-white tabular-nums">{fmt(value)}</span>
      </div>
      <div className="h-1.5 bg-zinc-900 overflow-hidden">
        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
